import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { canViewAgencies } from '../../utils/agencyAccess';
import ListLoadError from '../../components/admin/ListLoadError';
import styles from './CSVUploadsPage.module.css';

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }
  return `${size.toFixed(1)} ${units[unitIndex]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const CSVUploadsPage = () => {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();

  /**
   * A `?csvPageId=` deep link arrives from the Upload Portals list, which links
   * a portal to the uploads it received. It seeds the portal filter so the page
   * LANDS on that portal's uploads rather than on all of them. Read from the
   * same searchParams the tab hub uses, so one URL carries both.
   */
  const linkedPageId = searchParams.get('csvPageId') || '';
  const [uploads, setUploads] = useState([]);
  const [pages, setPages] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // A filter list that FAILED to load is not a filter list that is EMPTY. Tracked
  // per dropdown because the two are fetched independently and can fail apart.
  const [pagesError, setPagesError] = useState(false);
  const [agenciesError, setAgenciesError] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [pageFilter, setPageFilter] = useState(linkedPageId);
  const [agencyFilter, setAgencyFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadPages = useCallback(async () => {
    setPagesError(false);
    try {
      const response = await api.get('/admin/csv-pages?limit=100');
      setPages(response.csvPages || []);
    } catch (error) {
      // Card 53553. The old line here was `console.log('No CSV pages found')` — an
      // assertion of ABSENCE on a path that only knows the request FAILED (500, 403,
      // offline). The empty <select> below then repeated that false claim on screen.
      console.error('[Admin] Failed to load CSV pages:', error?.message ?? error);
      setPagesError(true);
      setPages([]);
    }
  }, []);

  const { hasPermission } = useAuth();

  const loadAgencies = useCallback(async () => {
    // Gated on the permission the API already enforces (card 49621): without
    // agencies.view this request can only ever 403, and the old catch reported
    // that denial as "no agencies / API unavailable", which is false on both counts.
    if (!canViewAgencies(hasPermission)) {
      // A permission state, NOT a failure — this staffer has no agencies to filter
      // by, and saying 'could not load' would be false. Clear the flag, never set it.
      setAgenciesError(false);
      setAgencies([]);
      return;
    }
    setAgenciesError(false);
    try {
      const response = await api.get('/admin/agencies');
      setAgencies(response.agencies || []);
    } catch (error) {
      console.error('[Admin] Failed to load agencies:', error?.message ?? error);
      setAgenciesError(true);
      setAgencies([]);
    }
  }, [hasPermission]);

  const loadUploads = useCallback(async (page = 1, filters = {}) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.search) params.append('search', filters.search);
      if (filters.csvPageId) params.append('csvPageId', filters.csvPageId);
      if (filters.agencyId) params.append('agencyId', filters.agencyId);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await api.get(`/admin/csv-uploads?${params}`);
      setUploads(response.uploads || []);
      setPagination(response.pagination);
    } catch (error) {
      setLoadError(true);
      setUploads([]);
      toast.error('Failed to load upload history');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    setPageFilter(linkedPageId);
    loadUploads(1, { csvPageId: linkedPageId });
    loadPages();
    loadAgencies();
  }, [loadUploads, loadPages, loadAgencies, linkedPageId]);

  const getFilters = () => ({
    search,
    csvPageId: pageFilter,
    agencyId: agencyFilter,
    dateFrom,
    dateTo
  });

  const handleSearch = (e) => {
    e.preventDefault();
    loadUploads(1, getFilters());
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...getFilters(), [field]: value };

    switch (field) {
      case 'csvPageId':
        setPageFilter(value);
        break;
      case 'agencyId':
        setAgencyFilter(value);
        break;
      case 'dateFrom':
        setDateFrom(value);
        break;
      case 'dateTo':
        setDateTo(value);
        break;
      default:
        break;
    }

    loadUploads(1, newFilters);
  };

  const handleDeactivate = async (uploadId) => {
    if (!confirm('Are you sure you want to deactivate this upload?')) return;

    try {
      await api.delete(`/admin/csv-uploads/${uploadId}`);
      toast.success('Upload deactivated');
      loadUploads(pagination?.page || 1, getFilters());
    } catch (error) {
      toast.error('Failed to deactivate upload');
    }
  };

  const handleViewData = (upload) => {
    const url = `/csv/${upload.csvPage?.slug}/data/${upload.id}`;
    window.open(url, '_blank');
  };

  const handleDownload = (upload) => {
    // The file path is stored but we don't have a direct download endpoint
    // For now, just show a message
    toast.info('Download feature coming soon');
  };

  const clearFilters = () => {
    setSearch('');
    setPageFilter('');
    setAgencyFilter('');
    setDateFrom('');
    setDateTo('');
    loadUploads(1, {});
  };

  const hasActiveFilters = search || pageFilter || agencyFilter || dateFrom || dateTo;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Upload History</h1>
          <p>View all CSV uploads across pages</p>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className={styles.filters}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <Input
            placeholder="Search by filename..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit">Search</Button>
        </form>

        <div className={styles.filterRow}>
          <select
            className={styles.select}
            value={pageFilter}
            onChange={(e) => handleFilterChange('csvPageId', e.target.value)}
          >
            <option value="">All Pages</option>
            {pagesError && (
              <option value="" disabled>
                Portals could not be loaded — this is not the same as having none
              </option>
            )}
            {pages.map((page) => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>

          <select
            className={styles.select}
            value={agencyFilter}
            onChange={(e) => handleFilterChange('agencyId', e.target.value)}
          >
            <option value="">All Agencies</option>
            {agenciesError && (
              <option value="" disabled>
                Agencies could not be loaded — this is not the same as having none
              </option>
            )}
            {agencies.map((agency) => (
              <option key={agency.id} value={agency.id}>
                {agency.name}
              </option>
            ))}
          </select>

          <div className={styles.dateRange}>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              placeholder="From"
            />
            <span className={styles.dateSeparator}>to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              placeholder="To"
            />
          </div>

          {hasActiveFilters && (
            <Button variant="outline" size="small" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Uploads Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner />
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Filename</th>
                <th>Page</th>
                <th>Agency</th>
                <th>Rows</th>
                <th>Size</th>
                <th>Uploaded By</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((upload) => (
                <tr key={upload.id}>
                  <td>
                    <div className={styles.filename}>{upload.fileName}</div>
                    {upload.columnHeaders && (
                      <div className={styles.columnCount}>
                        {upload.columnHeaders.length} columns
                      </div>
                    )}
                  </td>
                  <td>
                    {upload.csvPage ? (
                      <span className={styles.pageName}>{upload.csvPage.name}</span>
                    ) : (
                      <span className={styles.noValue}>—</span>
                    )}
                  </td>
                  <td>
                    {upload.csvPage?.agency ? (
                      <span className={styles.agencyBadge}>
                        {upload.csvPage.agency.name}
                      </span>
                    ) : (
                      <span className={styles.noValue}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={styles.rowCount}>
                      {upload.rowCount?.toLocaleString() || 0}
                    </span>
                  </td>
                  <td>
                    <span className={styles.fileSize}>
                      {formatFileSize(upload.fileSize)}
                    </span>
                  </td>
                  <td>
                    {upload.uploader ? (
                      <span className={styles.uploaderEmail}>{upload.uploader.email}</span>
                    ) : (
                      <span className={styles.noValue}>Anonymous</span>
                    )}
                  </td>
                  <td>
                    <span className={styles.uploadDate}>
                      {formatDate(upload.uploadedAt)}
                    </span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={styles.actionBtn}
                        onClick={() => handleViewData(upload)}
                        title="View Data"
                      >
                        View
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.danger}`}
                        onClick={() => handleDeactivate(upload.id)}
                        title="Deactivate"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && loadError && (
          <ListLoadError
            message="Could not load upload history"
            onRetry={() => loadUploads(1, getFilters())}
          />
        )}

        {!loading && !loadError && uploads.length === 0 && (
          <div className={styles.empty}>
            {hasActiveFilters
              ? 'No uploads match your filters'
              : 'No uploads yet'
            }
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className={styles.pagination}>
          <Button
            variant="outline"
            size="small"
            disabled={pagination.page <= 1}
            onClick={() => loadUploads(pagination.page - 1, getFilters())}
          >
            Previous
          </Button>
          <span>Page {pagination.page} of {pagination.totalPages}</span>
          <Button
            variant="outline"
            size="small"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => loadUploads(pagination.page + 1, getFilters())}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default CSVUploadsPage;
